import Vue from 'vue'
import { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'
import { TrainingCardInfo } from '@src/components/card/training-card/training-card-info/training-card-info'
import { WorkoutCardHistory } from '@src/components/card/workout-card/workout-card-history/workout-card-history'
import { DatePicker } from '@src/elements/input/date-picker'
import WorkoutData from '@src/data/workout-data'
import TrainingPlan from '@src/data/training-plan'
import { ExerciseResult } from '@src/data/workout-results'
import { WorkoutService } from '@src/service/workout'
import { TrainingPlanService } from '@src/service/training-plan'
import { MemberProfile } from '@src/data/member-profile'
import { convertToLocalDate, convertYYYYMMDDToDate } from '@src/util/date-format'
import { calculateScoreAverage } from '@src/util/functions'
import { Pagination } from '@src/elements/pagination/pagination'
import { DEFAULT_PAGE_SIZE } from '@src/util/constants'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Body1 } from '@src/elements/typography/typography'

import './history.sass'

export class DailyResults {
  id: number
  repetitions: number
  totalRepetitions: number
  pixformanceScore: number
  precisionScore: number
  intensityRating: number
  rangeScore: number
  tempoScore: number
  exerciseResults: ExerciseResult[]

  constructor (attributes) {
    this.id = attributes.id
    this.repetitions = attributes.exerciseResults.map((exerciseResult) => (exerciseResult.repetitionCount)).reduce((sum, current) => (sum + current), 0)
    this.totalRepetitions = attributes.exerciseResults.map((exerciseResult) => (exerciseResult.repetitionGoal)).reduce((sum, current) => (sum + current), 0)
    this.pixformanceScore = attributes.pixformanceScore
    this.precisionScore = attributes.precisionScore
    this.intensityRating = attributes.intensityRating
    this.rangeScore = attributes.rangeScore
    this.tempoScore = attributes.tempoScore
    this.exerciseResults = attributes.exerciseResults.map(result => new ExerciseResult(camelcaseKeys(result)))
  }
}

export class DailyWorkoutResult {
  id: number
  repetitions: number
  totalRepetitions: number
  pixformanceScore: number
  precisionScore: number
  intensityRating: number
  rangeScore: number
  tempoScore: number
  exerciseCount: number
  workoutId: number
  exerciseResults: ExerciseResult[]
  day: string
  workout: WorkoutData

  constructor (attributes) {
    this.id = attributes.id
    this.pixformanceScore = attributes.pixformanceScore
    this.precisionScore = attributes.precisionScore
    this.intensityRating = attributes.intensityRating
    this.rangeScore = attributes.rangeScore
    this.tempoScore = attributes.tempoScore
    this.exerciseCount = attributes.exerciseResults.length
    this.workoutId = attributes.workoutId
    this.exerciseResults = attributes.exerciseResults.map(result => new ExerciseResult(camelcaseKeys(result)))
    this.repetitions = this.exerciseResults.reduce((sum, current) => (sum + current.repetitionCount!), 0)
    this.totalRepetitions = this.exerciseResults.reduce((sum, current) => (sum + current.repetitionGoal!), 0)

    this.day = attributes.day
    if (attributes.workout) this.workout = new WorkoutData(camelcaseKeys(attributes.workout))
  }
}

@Component({
  computed: {
    memberProfile () {
      return this.$store.state.memberProfile
    }
  }
})
export class TrainingHistory extends Vue {

  @Prop({ required: true, default: 'station_workouts' })
  historyType: string

  protected history: any[] = []

  private memberProfile: MemberProfile | null

  private workoutService: WorkoutService = new WorkoutService()
  private trainingPlanService: TrainingPlanService = new TrainingPlanService()
  private loading: boolean = true
  private currentPage: number = 1
  private totalNoOfPages: number = 1

  @Watch('historyType', { immediate: true })
  historyTypeChanged () {
    this.history = []
    return this.changePage(1)
  }

  updated () {
    if (this.memberProfile) {
      document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('PAGE.MEMBERS.DETAIL.TABS.TRAINING')}`
    } else {
      document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.DETAIL.TABS.TRAINING')}`
    }
  }

  updateList (itemId) {
    const index = this.history.findIndex(historyItem => historyItem.id === itemId)
    if (index > -1) {
      this.history.splice(index, 1)
    }
  }

  render (h: CreateElement) {
    return <div class='history-wrapper scroll-content'>
      {this.loading ? <Loader /> :
        <div class='list'>
          {this.history.length ? '' : <Body1 align='center' class='empty-message'>{this.$t('PAGE.MEMBERS.TRAINING.HISTORY.NO_HISTORY')}</Body1>}
          <div class='history-list'>
            {this.history.map((item) => this.mapItem(item, h))}
          </div>
          <Pagination changePage={this.changePage}
                      currentPage={this.currentPage}
                      pageSize={DEFAULT_PAGE_SIZE}
                      total={this.totalNoOfPages * DEFAULT_PAGE_SIZE} />
        </div>}
    </div>
  }

  changePage (page) {
    this.currentPage = page
    this.loading = true
    return this.historyType === 'training_plans' ? this.loadTrainingPlans() : this.loadWorkouts()
  }

  mapItem (item, h) {
    return this.historyType === 'training_plans' ? this.mapTrainingPlanItem(item, h) : this.mapWorkoutItem(item, h)
  }

  mapWorkoutItem ({ workout, result, id, date, type }, h: CreateElement) {
    return (
      <WorkoutCardHistory
        workout={workout}
        result={result}
        date={date}
        type={type}
        key={id + date}
        changeIntensity={this.changeIntensity}
        memberProfile={this.memberProfile}
      />
    )
  }

  changeIntensity (resultDate, value: number) {
    this.history.forEach((element) => {
      if (convertToLocalDate(element.result.day) === resultDate) element.result.intensityRating = value
    })
  }

  loadWorkouts () {
    return this.workoutService.getWorkoutResults(this.$route.params.id, this.currentPage, DEFAULT_PAGE_SIZE, null, null, true)
      .then((response) => {
        let composeWorkoutResults = this.composeWorkoutResults(response.workoutResults)
        this.history = composeWorkoutResults
          .map(result => ({ result, workout: result.workout, date: convertToLocalDate(result.day), type: 'history', id: result.workoutId }))
        this.totalNoOfPages = response.totalPages
      })
      .finally(() => this.loading = false)
  }

  // ------------ WORKOUTS

  protected composeWorkoutResults (workoutResults) {
    if (workoutResults.length === 0) {
      return []
    }

    let workoutsHistory: any[] = []
    let workoutIds: number[] = []

    // the results are all grouped by day
    workoutResults.forEach(daily => {
      let dailyResults = new DailyResults(daily)

      // group the results in a day, by workout
      let workoutResults = daily.exerciseResults.reduce((group, exerciseResults) => {
        if (!group[exerciseResults.workout_id]) {
          group[exerciseResults.workout_id] = []
        }
        group[exerciseResults.workout_id].push(exerciseResults)

        return group
      }, {})

      // return an entry for each day-workout-exercises group
      Object.entries(workoutResults).forEach(([id, value]) => {
        let workoutId = Number(id)

        // compute a list with unique workout ids, so a workout will only be retrieved once
        if (!workoutIds.includes(workoutId)) {
          workoutIds.push(workoutId)
        }

        // compute the average scores for the day, placing each score indicator in a key value pair
        const scores = ['pixformance_score', 'tempo_score', 'precision_score', 'range_score']
          .map((key) => ([ key, calculateScoreAverage(value,key) ]))
          .reduce((object, value) => Object.assign(object, { [value[0]]: value[1] }), {})

        // add an entry for each workout in a day and it's exercise results (same day)
        workoutsHistory.push(new DailyWorkoutResult({
          ...dailyResults,
          ...camelcaseKeys(scores),
          workoutId: workoutId,
          workout: workoutResults[workoutId][0].workout,
          exerciseResults: workoutResults[workoutId],
          exerciseCount: workoutResults[workoutId].length,
          day: workoutResults[workoutId][0].started_at
        }))

      })
    })

    // sort by date
    return workoutsHistory.sort((a, b) => a.day < b.day ? 1 : a.day > b.day ? -1 : 0)
  }

  // ------------ TRAINING PLANS

  protected mapTrainingPlanItem (trainingPlan, h: CreateElement) {
    let formattedStartDate = convertYYYYMMDDToDate(trainingPlan.startDate)
    let startDate = convertToLocalDate(formattedStartDate)
    let formattedEndDate = convertYYYYMMDDToDate(trainingPlan.endDate)
    let endDate = convertToLocalDate(formattedEndDate)
    return <div class='training-plan-history-item' key={trainingPlan.id}>
      <div class='date-pickers' style={{ marginTop: '10px' }}>
        <DatePicker readonly={true} allowPastDate={false} type='date' value={startDate}
                    label={`${this.$t('START_DATE')}:`} class='readonly'/>
        <DatePicker readonly={true} allowPastDate={false} type='date' value={endDate}
                    label={`${this.$t('END_DATE')}:`} class='readonly'/>
      </div>
      <TrainingCardInfo training={new TrainingPlan(trainingPlan)} type='history' updateCallback={this.updateList} />
    </div>
  }

  protected loadTrainingPlans () {
    return this.trainingPlanService.getTrainingPlans(this.currentPage, DEFAULT_PAGE_SIZE, 'member', undefined, parseInt(this.$route.params.id, 0), true)
      .then((response) => {
        this.history = response.trainingPlans
        this.totalNoOfPages = response.totalPages
      })
      .finally(() => this.loading = false)
  }
}
